"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

const SheetContext = React.createContext<{
    open: boolean
    onOpenChange: (open: boolean) => void
}>({ open: false, onOpenChange: () => { } })

export const Sheet = ({ children, open = false, onOpenChange }: {
    children: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
}) => {
    return (
        <SheetContext.Provider value={{ open, onOpenChange: onOpenChange || (() => { }) }}>
            {children}
        </SheetContext.Provider>
    )
}

export const SheetTrigger = ({ children, asChild }: { children: React.ReactNode, asChild?: boolean }) => {
    const { onOpenChange } = React.useContext(SheetContext)
    return (
        <div onClick={() => onOpenChange(true)} className="cursor-pointer">
            {children}
        </div>
    )
}

const sheetVariants = cva(
    "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
    {
        variants: {
            side: {
                top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
                bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
                left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
                right: "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
            },
        },
        defaultVariants: {
            side: "left",
        },
    }
)

interface SheetContentProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sheetVariants> { }

export const SheetContent = ({ side = "left", className, children, ...props }: SheetContentProps) => {
    const { open, onOpenChange } = React.useContext(SheetContext)

    // Lock body scroll
    React.useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => { document.body.style.overflow = 'unset' }
    }, [open])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex">
            <div
                className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm transition-all duration-100 data-[state=closed]:fade-out data-[state=open]:fade-in"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onOpenChange(false);
                }}
                aria-hidden
            />
            <div
                className={cn(sheetVariants({ side }), className, "z-50 h-full relative")}
                {...props}
                data-state={open ? "open" : "closed"}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            >
                {children}
                <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
                    aria-label="Fechar"
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </button>
            </div>
        </div>
    )
}
